import { Link } from "react-router-dom";
import styled from "styled-components";

// Общие стили раздела «Заявки»: только базовые цвета темы (searchButtonBg, white, input.*), которые есть во всех сайтах.

export const PageWrapper = styled("div")(({ theme }) => ({
  backgroundColor: "#f5f7fa",
  flexGrow: 1,
  paddingBottom: 50,
  minHeight: "70vh",
}));

export const PageHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterBetween,
  flexWrap: "wrap",
  gap: 15,
  ...theme.paddings(20, 0, 15),
}));

export const PageTitle = styled("h1")(({ theme }) => ({
  margin: 0,
  fontSize: 30,
  fontWeight: 600,
  color: "#1a1a1a",

  [theme.down("sm")]: {
    fontSize: 22,
  },
}));

export const PageSubtitle = styled("div")(({ theme }) => ({
  marginTop: 6,
  fontSize: 15,
  color: "#666",

  [theme.down("sm")]: {
    fontSize: 13,
  },
}));

export const HeaderActions = styled("div")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  flexWrap: "wrap",
  gap: 10,
}));

export const Card = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 18,
  width: "100%",
  ...theme.paddings(24),
  borderRadius: 12,
  backgroundColor: theme.palette.white,
  border: "1px solid #e5e8eb",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
  boxSizing: "border-box",

  [theme.down("sm")]: {
    ...theme.paddings(16, 14),
  },
}));

export const NarrowCard = styled(Card)(({ theme }) => ({
  maxWidth: 560,
  margin: "30px auto 0",

  [theme.down("sm")]: {
    marginTop: 15,
  },
}));

export const CardTitle = styled("h2")(({ theme }) => ({
  margin: 0,
  fontSize: 22,
  fontWeight: 700,
  color: "#333",

  [theme.down("sm")]: {
    fontSize: 19,
  },
}));

export const CardText = styled("p")(({ theme }) => ({
  margin: 0,
  fontSize: 14,
  lineHeight: 1.5,
  color: "#666",
}));

export const Fields = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 14,
}));

export const Row = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 14,

  [theme.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

export const Buttons = styled("div")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  flexWrap: "wrap",
  gap: 10,
}));

const buttonBase = theme => ({
  ...theme.mixins.flexCenterCenter,
  gap: 8,
  position: "relative",
  height: 42,
  ...theme.paddings(0, 18),
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "all .2s ease",
  textDecoration: "none",
  boxSizing: "border-box",

  "&:disabled": {
    opacity: 0.6,
    cursor: "default",
  },

  "& svg": {
    fontSize: 18,
    flexShrink: 0,
  },
});

export const PrimaryButton = styled("button")(({ theme }) => ({
  ...buttonBase(theme),
  border: "none",
  backgroundColor: theme.palette.searchButtonBg,
  color: theme.palette.white,

  "&:hover:not(:disabled)": {
    filter: "brightness(90%)",
  },
}));

export const SecondaryButton = styled("button")(({ theme }) => ({
  ...buttonBase(theme),
  border: `1px solid ${theme.palette.searchButtonBg}`,
  backgroundColor: "transparent",
  color: theme.palette.searchButtonBg,

  "&:hover:not(:disabled)": {
    backgroundColor: theme.palette.searchButtonBg,
    color: theme.palette.white,
  },
}));

export const TextButton = styled("button")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: 6,
  padding: 0,
  border: "none",
  background: "none",
  color: theme.palette.searchButtonBg,
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",

  "&:hover:not(:disabled)": {
    textDecoration: "underline",
  },

  "&:disabled": {
    color: "#999",
    cursor: "default",
  },
}));

export const TextLink = styled(Link)(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: 6,
  color: theme.palette.searchButtonBg,
  fontSize: 14,
  fontWeight: 500,
  textDecoration: "none",

  "&:hover": {
    textDecoration: "underline",
  },
}));

export const ButtonLoadingCover = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  position: "absolute",
  inset: 0,
  borderRadius: 6,
  backgroundColor: theme.palette.searchButtonBg,
}));

export const FormError = styled("div")(({ theme }) => ({
  ...theme.paddings(10, 14),
  borderRadius: 6,
  backgroundColor: theme.palette.input.errorBg,
  border: `1px solid ${theme.palette.input.borderError}`,
  color: theme.palette.input.errorText,
  fontSize: 14,
  lineHeight: 1.4,
}));

export const Notice = styled("div")(({ theme, $variant }) => {
  const variants = {
    info: { backgroundColor: "#eef4ff", border: "1px solid #c9dafc", color: "#1e3a8a" },
    success: { backgroundColor: "#ecfdf3", border: "1px solid #b7ebc6", color: "#166534" },
    warning: { backgroundColor: "#fff7e6", border: "1px solid #ffd591", color: "#ad4e00" },
    muted: { backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb", color: "#4b5563" },
  };

  return {
    ...theme.mixins.flexAlignCenter,
    flexWrap: "wrap",
    gap: 10,
    ...theme.paddings(10, 14),
    borderRadius: 8,
    fontSize: 14,
    lineHeight: 1.45,
    ...(variants[$variant] || variants.info),
  };
});

export const Loading = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  minHeight: 160,
}));

// ---- поля ----

export const SelectBlock = styled("div")(({ theme, $isError }) => ({
  ...theme.mixins.flexColumn,
  position: "relative",
  height: 50,
  borderRadius: 4,
  border: `1px solid ${$isError ? theme.palette.input.borderError : theme.palette.input.border}`,
  ...($isError ? { backgroundColor: theme.palette.input.errorBg } : {}),
  transition: "all .2s ease",

  "&:focus-within": {
    borderColor: $isError ? theme.palette.input.borderError : theme.palette.input.borderFocus,
    boxShadow: `0 0 0 3px ${$isError ? theme.palette.input.errorShadow : theme.palette.input.focusShadow}`,
  },

  "& label": {
    fontSize: 10,
    paddingLeft: 15,
    paddingTop: 2,
    color: theme.palette.input.labelText,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    "& span": {
      color: theme.palette.input.errorText,
    },
  },

  "& select": {
    ...theme.paddings(14, 30, 0, 11),
    position: "absolute",
    inset: 0,
    width: "100%",
    border: "none",
    backgroundColor: "transparent",
    fontSize: 16,
    fontFamily: "inherit",
    color: "#333",
    cursor: "pointer",
    outline: "none",

    [theme.down("sm")]: {
      fontSize: 14,
    },
  },
}));

export const FieldError = styled("div")(({ theme }) => ({
  color: theme.palette.input.errorText,
  fontSize: 13,
  marginTop: 4,
  fontWeight: 500,
}));

export const FieldHint = styled("div")(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.input.labelText,
  marginTop: 4,
}));

export const ReadonlyField = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterBetween,
  gap: 10,
  ...theme.paddings(12, 15),
  borderRadius: 8,
  backgroundColor: "#f5f7fa",
  fontSize: 14,
  color: "#333",

  "& b": {
    fontSize: 16,
  },
}));

// ---- файлы ----

export const FilesBlock = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 8,

  "& input[type=file]": {
    display: "none",
  },
}));

export const FileChips = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
}));

export const FileChip = styled("div")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: 6,
  maxWidth: "100%",
  ...theme.paddings(5, 8, 5, 10),
  borderRadius: 16,
  backgroundColor: "#f1f5f9",
  border: "1px solid #e2e8f0",
  fontSize: 13,
  color: "#334155",

  "& span": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 220,
  },

  "& small": {
    color: "#94a3b8",
  },

  "& button": {
    ...theme.mixins.flexCenterCenter,
    padding: 0,
    border: "none",
    background: "none",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 16,
  },
}));

// ---- список заявок ----

export const FilterTabs = styled("div")(({ theme }) => ({
  display: "inline-flex",
  padding: 3,
  borderRadius: 8,
  backgroundColor: "#e9edf2",
}));

export const FilterTab = styled("button")(({ theme, $active }) => ({
  ...theme.paddings(7, 16),
  border: "none",
  borderRadius: 6,
  backgroundColor: $active ? theme.palette.white : "transparent",
  boxShadow: $active ? "0 1px 3px rgba(0, 0, 0, 0.12)" : "none",
  color: $active ? "#1a1a1a" : "#555",
  fontSize: 14,
  fontWeight: $active ? 600 : 500,
  cursor: "pointer",
}));

export const TicketList = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 10,
  marginTop: 15,
}));

export const TicketItem = styled(Link)(({ theme, $waiting, $unread }) => ({
  display: "flex",
  alignItems: "center",
  gap: 15,
  ...theme.paddings(16, 18),
  borderRadius: 10,
  backgroundColor: $waiting ? "#fffaf0" : theme.palette.white,
  border: `1px solid ${$waiting ? "#fcd34d" : "#e5e8eb"}`,
  borderLeft: `4px solid ${$waiting ? "#f59e0b" : $unread ? theme.palette.searchButtonBg : "#e5e8eb"}`,
  textDecoration: "none",
  color: "inherit",
  transition: "box-shadow .2s ease",

  "&:hover": {
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
  },

  [theme.down("sm")]: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 8,
    ...theme.paddings(14),
  },
}));

export const TicketMain = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 5,
  flex: 1,
  minWidth: 0,
}));

export const TicketTop = styled("div")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  flexWrap: "wrap",
  gap: 8,
  fontSize: 13,
  color: "#64748b",
}));

export const TicketSubject = styled("div")(({ theme, $bold }) => ({
  fontSize: 16,
  fontWeight: $bold ? 700 : 600,
  color: "#1a1a1a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
}));

export const TicketMeta = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "4px 14px",
  fontSize: 13,
  color: "#64748b",
}));

export const TicketSide = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  alignItems: "flex-end",
  gap: 6,
  flexShrink: 0,

  [theme.down("sm")]: {
    flexDirection: "row",
    alignItems: "center",
  },
}));

export const StatusBadge = styled("span")(({ $color, $background }) => ({
  display: "inline-flex",
  alignItems: "center",
  height: 24,
  padding: "0 10px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  color: $color || "#546e7a",
  backgroundColor: $background || "rgba(84, 110, 122, 0.12)",
}));

export const CountBadge = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 20,
  height: 20,
  padding: "0 6px",
  borderRadius: 10,
  backgroundColor: "#ef4444",
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  boxSizing: "border-box",
}));

export const EmptyState = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterCenter,
  flexDirection: "column",
  gap: 12,
  minHeight: 180,
  ...theme.paddings(30),
  borderRadius: 10,
  backgroundColor: theme.palette.white,
  border: "1px solid #e5e8eb",
  color: "#666",
  fontSize: 16,
  textAlign: "center",

  "& svg": {
    fontSize: 40,
    color: "#b0bec5",
  },
}));

// ---- чат ----

export const ChatHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 8,
}));

export const Chat = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 12,
  ...theme.paddings(16),
  borderRadius: 10,
  backgroundColor: "#f8fafc",
  border: "1px solid #eef2f6",
  maxHeight: "60vh",
  overflowY: "auto",

  [theme.down("sm")]: {
    ...theme.paddings(10),
    maxHeight: "none",
  },
}));

export const MessageRow = styled("div")(({ $mine }) => ({
  display: "flex",
  justifyContent: $mine ? "flex-end" : "flex-start",
}));

export const Bubble = styled("div")(({ theme, $mine }) => ({
  ...theme.mixins.flexColumn,
  gap: 6,
  maxWidth: "78%",
  ...theme.paddings(10, 14),
  borderRadius: 12,
  borderTopRightRadius: $mine ? 4 : 12,
  borderTopLeftRadius: $mine ? 12 : 4,
  backgroundColor: $mine ? "#e8f0fe" : theme.palette.white,
  border: `1px solid ${$mine ? "#cfe0fc" : "#e5e8eb"}`,
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",

  [theme.down("sm")]: {
    maxWidth: "92%",
  },
}));

export const MessageAuthor = styled("div")(({ theme, $staff }) => ({
  fontSize: 12,
  fontWeight: 700,
  color: $staff ? theme.palette.searchButtonBg : "#475569",
}));

export const MessageBody = styled("div")(({ theme }) => ({
  fontSize: 15,
  lineHeight: 1.5,
  color: "#1f2937",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",

  "& a": {
    color: "#1d4ed8",
    textDecoration: "underline",
  },
}));

export const MessageTime = styled("div")(({ $mine }) => ({
  fontSize: 11,
  color: "#94a3b8",
  textAlign: $mine ? "right" : "left",
}));

export const SystemEvent = styled("div")(({ theme }) => ({
  alignSelf: "center",
  maxWidth: "90%",
  ...theme.paddings(4, 12),
  borderRadius: 12,
  backgroundColor: "#eef2f6",
  color: "#64748b",
  fontSize: 12,
  textAlign: "center",
}));

export const Attachments = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
}));

export const AttachmentButton = styled("button")(({ theme }) => ({
  ...theme.mixins.flexAlignCenter,
  gap: 6,
  maxWidth: "100%",
  ...theme.paddings(5, 10),
  borderRadius: 6,
  border: "1px solid #dbe3ec",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: 13,
  cursor: "pointer",

  "& span": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 200,
  },

  "& small": {
    color: "#94a3b8",
  },

  "&:hover:not(:disabled)": {
    borderColor: theme.palette.searchButtonBg,
  },

  "&:disabled": {
    opacity: 0.6,
    cursor: "default",
  },
}));

export const ReplyBox = styled("div")(({ theme }) => ({
  ...theme.mixins.flexColumn,
  gap: 10,
}));

export const ReplyActions = styled("div")(({ theme }) => ({
  ...theme.mixins.flexCenterBetween,
  flexWrap: "wrap",
  gap: 10,
}));

export const InfoGrid = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "6px 18px",
  fontSize: 13,
  color: "#64748b",

  "& b": {
    color: "#334155",
    fontWeight: 600,
  },
}));

export const NavBadge = styled("span")(({ $inline }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 18,
  height: 18,
  padding: "0 5px",
  marginLeft: $inline ? 6 : 0,
  borderRadius: 9,
  backgroundColor: "#ef4444",
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  verticalAlign: "middle",
  boxSizing: "border-box",
}));
